import React, { useEffect, useState, useRef } from "react";
import { message } from "antd";
import WidgetHeader from "../../../components/WidgetHeader";
import {
  describeDataFilters,
  describeGlobalFilters,
  exportChartToCsv,
  exportChartToXlsx,
  buildNodePdfAttachment,
  exportNodeToPdf,
  exportNodeToPng,
} from "../../../utils/widgetExport";
import moment from "moment";
import ReactApexChart from "react-apexcharts";
import { chartHelper } from "../../../utils/chartHelper";
import { buildCartesianChartOptions, buildDonutChartOptions } from "../../../utils/chartOptions";
import * as _ from 'lodash';
import { DATE_FORMAT } from "../../../constants/dateConstants";
import { returnDataArrayObject } from "../../../utils/arrayHelper";

/* `filterResource` is this widget's OWN resource, carrying the global filters with the
   values the reader currently has set - as distinct from `selectedResource`, which is
   whichever resource the board is working with. Only the export heading reads it, to
   name the date ranges a file was taken under. */
const BoardChartContainer = ({ reportData, charts, availableFields, dataFilters, resourceId, selectedResource, filterResource, filtersNode, aggregation, widgetName, widgetProperties, hideHeader, boardName, onRegisterExport, onRegisterEmailAttachment, periodKeys, periodLabels, matchBy }) => {

  const [selectedValues, setSelectedValues] = useState([])
  // the element the per-widget Full screen button expands
  const widgetRef = useRef(null);
  // just the chart itself - what PDF and PNG photograph, without the header bar above it
  const chartBodyRef = useRef(null);
  const [selectedFilters, setDataFilters] = useState([]);

  const [displayData, setDisplayData] = useState([]);
  const [fieldNames, setFieldNames] = useState([]);

  useEffect(() => {
    if(dataFilters && selectedResource.resourceId === charts.resourceId){
      setDataFilters(dataFilters);
    }
  }, [dataFilters])

  /* `list || []` guards null and undefined but NOT an object, which is the shape that
     actually turns up: a response the extractor could not reduce to rows arrives here
     whole, and `object.reduce is not a function` kills the render. Anything that is not
     an array groups to nothing, which draws an empty chart instead. */
  const groupBy = (list, props) => {
    if (!Array.isArray(list)) return {};
    return list.reduce((a, b) => {
      (a[b[props]] = a[b[props]] || []).push(b);
      return a;
    }, {});
  };

  /* Sum a group, but keep "no figure" distinct from "zero".

     An outer join leaves one period null wherever the other had a row it did not.
     `Number(null)` is 0, so every one of those was being totalled as a real zero -
     the tooltip said "Period 1: 0" for a category that period never reported, and on
     a bar chart a zero-height bar is simply invisible, so it read as measured-at-
     nothing rather than not-returned.

     A group whose values are ALL null stays null: ApexCharts draws a gap and leaves
     the series out of that tooltip. This data has real zeros (TotalVat, VatRate), so
     the two have to stay tellable apart - the same reason the table shows a dash. */
  var getTotal = (values, fieldName) => Object.keys(values).reduce((a, r) => {
    const present = values[r].filter((val) => val[fieldName] !== null && val[fieldName] !== undefined);
    a[r] = present.length ? present.reduce((acc, val) => acc + Number(val[fieldName]), 0) : null;
    return a;
  }, {});

  /* ── THE X AXIS IS ALPHABETICAL ───────────────────────────────────────────────
     Object keys come out in insertion order, so without this the axis is in whatever
     order the API returned the rows - and this API does not return them the same way
     twice. See the note in utils/arrayHelper.js on why "Match rows by" exists: one
     response came back MAINS/STARTER/SIDE and another SIDE/MAINS/STARTER. The chart
     changed shape between a first load and a refetch with identical figures, and the
     editor and the view page disagreed with each other over the same report.

     Sorting here settles all of it: the same order on every render, the same order in
     the editor as on the board, and - because both periods of a comparison are grouped
     from the same rows and sorted the same way - two series that line up on one axis.

     Every series on this widget goes through here, so none of them can drift from the
     categories drawn beneath them. */
  const getValueArray = (values) =>
    Object.keys(values)
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((val) => ({ name: val, value: values[val] }));

  const filterByDate = (filter, date, format) => {
    if (filter.filterType === 'between') {
      return moment(date).isBetween(moment(filter.value1), moment(filter.value2));
    } else if (filter.filterType === 'lte') {
      return moment(moment(date).format(format || DATE_FORMAT)).isSameOrBefore(moment(filter.value).format(format || DATE_FORMAT));
    } else if (filter.filterType === 'lt') {
      return moment(moment(date).format(format || DATE_FORMAT)).isBefore(moment(filter.value).format(format || DATE_FORMAT));
    } else if (filter.filterType === 'gte') {
      return moment(moment(date).format(format || DATE_FORMAT)).isSameOrAfter(moment(filter.value).format(format || DATE_FORMAT));
    } else if (filter.filterType === 'gt') {
      return moment(moment(date).format(format || DATE_FORMAT)).isAfter(moment(filter.value).format(format || DATE_FORMAT));
    } else {
      return moment(moment(date).format(format || DATE_FORMAT)).isSame(moment(filter.value).format(format || DATE_FORMAT))
    }
  }

  const filterByNumber = (filter, value) => {
    if (filter.filterType === 'between') {
      return value >= Number(filter.value1) && value <= Number(filter.value2)
    } else if (filter.filterType === 'lte') {
      return value <= Number(filter.value);
    } else if (filter.filterType === 'lt') {
      return value < Number(filter.value);
    } else if (filter.filterType === 'gte') {
      return value >= Number(filter.value);
    } else if (filter.filterType === 'gte') {
      return value >= Number(filter.value);
    } else if (filter.filterType === 'gt') {
      return value > Number(filter.value);
    } else {
      return value === Number(filter.value)
    }
  };

  const filterByString = (filter, value) => {
    return value === filter.value
  }

  const filterValues = (field) => {
    let results = reportData.data || []
    selectedFilters?.forEach((selectedFilter) => {
      if (field !== selectedFilter.fieldName) {
        results = results.filter((value) => {
          if (selectedFilter.dataType === 'date') {
            const format  = availableFields?.find(
              (f) => f?.fieldName === selectedFilter.fieldName
            )?.format
            return filterByDate(selectedFilter, value[selectedFilter.fieldName], format)
          }
          if (selectedFilter.dataType === 'number') {
            return filterByNumber(selectedFilter, value[selectedFilter.fieldName])
          }
          if (selectedFilter.dataType === 'string') {
            return filterByString(selectedFilter, value[selectedFilter.fieldName])
          }
        });
      };
    })

    return results;
  };

  const formatValues = (array, fieldName) => {
    const reg = /^\d*\.?\d*$/;
    if (array && Boolean(array.length)) {
      if (!reg.test(array[0][fieldName]) && _.isDate(new Date(array[0][fieldName])) && moment(array[0][fieldName]).isValid()) {
        return array.map((value) => Object.assign({}, value, { [fieldName]: moment(value[fieldName])
          .format(availableFields?.find(availbleField => availbleField.fieldName === fieldName)?.format || "YYYY-MM-DD") }))
      }
    }
    return array;
  }

  useEffect(() => {
    // const selectedResults = Boolean(selectedFilters?.length) ? filterValues(charts?.fieldName?.fieldName) : reportData.data;
    const fieldDataFilter = selectedFilters?.find(filter => filter.fieldName === charts?.fieldName?.fieldName)

    if (charts?.fieldName?.fieldName && charts?.groupBy?.fieldName) {
      selectedFilters?.length && fieldDataFilter && Boolean(Object.values(fieldDataFilter).length)
        ? setSelectedValues(
          getValueArray(
            getTotal(
              groupBy(formatValues(displayData, charts?.groupBy?.fieldName), charts?.groupBy?.fieldName)
              , charts?.fieldName?.fieldName
            )).filter(value => filterByNumber(fieldDataFilter, value.value)))
        :
        setSelectedValues(
          getValueArray(
            getTotal(
              groupBy(formatValues(displayData, charts?.groupBy?.fieldName), charts?.groupBy?.fieldName)
              , charts?.fieldName?.fieldName
            )));
    }
  }, [charts, selectedFilters, displayData])


  useEffect(() => {
    let aggregatedData = [];
    let reportDataArr = JSON.parse(JSON.stringify(returnDataArrayObject(reportData))).data; //displayData.data.map(x => x)
    const selectedResults = Boolean(dataFilters?.length) ? filterValues(charts?.fieldName?.fieldName) : reportDataArr || reportData;
    if (
      availableFields &&
      aggregation &&
      aggregation.row &&
      aggregation.row.length > 0
    ) {
      
      Object.preventExtensions(selectedResults);

      selectedResults.forEach((rd, i) => {
        console.log("aggregation DataTable=>", aggregation);

        aggregation.row.forEach((ag, j) => {
          if (ag.rowAggregation[0].operator === "add_c") {
            console.log("====if addc===", rd[ag.rowAggregation[0].fieldName]);
            selectedResults[i][ag?.displayName] =
              Number(rd[ag.rowAggregation[0].fieldName]) +
              Number(ag.rowAggregation[0].const_1);
            console.log(
              selectedResults[i][ag.displayName]
            );
          } else if (ag.rowAggregation[0].operator === "multiply_c") {
            selectedResults[i][ag?.displayName] =
              Number(rd[ag.rowAggregation[0].fieldName]) *
              Number(ag.rowAggregation[0].const_1);
          } else if (ag.rowAggregation[0].operator === "add") {
            selectedResults[i][ag?.displayName] =
              Number(rd[ag.rowAggregation[0].fieldName]) +
              Number(selectedResults[i][ag.rowAggregation[0].field_2]);
          } else if (ag.rowAggregation[0].operator === "multiply") {
            selectedResults[i][ag?.displayName] =
              Number(rd[ag.rowAggregation[0].fieldName]) *
              Number(selectedResults[i][ag.rowAggregation[0].field_2]);
          } else if (ag.rowAggregation[0].operator === "sum") {
            selectedResults[i][ag?.displayName] = ag.rowAggregation[0].fieldNamesList
              .reduce((a, b) => {
                return a + Number(selectedResults[i][b]);
              }, 0)
              .toFixed(2);
          } else if (ag.rowAggregation[0].operator === "avg") {
            selectedResults[i][ag?.displayName] = (
              ag.rowAggregation[0].fieldNamesList.reduce((a, b) => {
                return a + Number(selectedResults[i][b]);
              }, 0) / ag.rowAggregation[0].fieldNamesList.length
            ).toFixed(2);
          } else if (ag.rowAggregation[0].operator === "func") {
            if (ag.rowAggregation[0].calculationStr) {
              console.log(
                "rd[ag.rowAggregation[0]====>>",
                ag.rowAggregation[0],
                "<<<<>>>>",
                rd[ag.rowAggregation[0]["seeing"]]
              );
              let fcn = ag.rowAggregation[0].calculationStr
                .replaceAll(" ", "")
                .replaceAll("@", "Number(rd['") // {field1} - {field2}        @field1 - @field2
                .replaceAll("$", "'])"); // Number(rd['field1']) - Number(rd['field2'])
              selectedResults[i][ag?.displayName] = Number(eval(fcn)).toFixed(2);
              return;
            }
          }
        });
      });


      setDisplayData(selectedResults);
    } else {
      setDisplayData(selectedResults);
    }
    /* `reportData` was missing from this list while the body reads it, so the chart
       kept whatever rows it saw on its FIRST render and ignored every refetch after
       - Apply, Reset, Refresh and auto-refresh all left it showing stale figures.
       Nothing to do with periods; it has been true of every chart. */
  }, [availableFields, aggregation, reportData]);


  const formatDate = (array) => {
    return chartHelper(array).numberFormat(2)
  }

  /* ── ONE SERIES PER PERIOD (PRM-518) ──────────────────────────────────────────
     A comparison carries the SAME fields for every period - only the window differs -
     so the author sets X and Y ONCE and each period becomes a line. There is
     deliberately no second axis config: it would always hold the same two fields, and
     letting them differ would plot Total Sales against VAT and call it a comparison.

     Period one reads the bare value field and the rest read the namespaced one (see
     utils/arrayHelper), which is the same swap the table makes on a column's dataIndex.

     The category axis is built ONCE, from the grouping, and shared - which is what
     keeps the lines on the same footing. That only holds if rows were matched on a key:
     without one they pair by position, and since the API returns the periods in
     different orders the second line would sit against the wrong categories. So a
     comparison chart is drawn only when a key is set; otherwise it stays single-series
     and shows period one, which is true rather than plausibly wrong. */
  const comparisonKeys = matchBy && (periodKeys || []).length > 1 ? periodKeys : [];

  /* Whether there is anything at all underneath the chart. Read off displayData, the
     array the series are built from, so the empty state and the drawing can never
     disagree - a chart that says "no data" while plotting something would be worse
     than the blank canvas it replaces. */
  const hasRowsToPlot = Array.isArray(displayData) && displayData.length > 0;

  const seriesForPeriods = () => {
    const valueField = charts?.fieldName?.fieldName;
    const groupField = charts?.groupBy?.fieldName;
    if (!valueField || !groupField) return null;

    return comparisonKeys.map((key, index) => ({
      name: (periodLabels || {})[key] || key,
      /* Grouped and summed per period, through the same helpers the single-series
         path uses - the only difference is which field is being totalled. */
      data: formatDate(
        getValueArray(
          getTotal(
            groupBy(formatValues(displayData, groupField), groupField),
            index === 0 ? valueField : `${key}__${valueField}`,
          ),
        )?.map((value) => value.value),
      ),
    }));
  };

  /* ── A LINE IS DRAWN THROUGH ZERO, NOT BROKEN AT IT ──────────────────────────
     LINE CHARTS ONLY. Everywhere else a figure a period never reported stays null:
     the table shows a dash, the sheet leaves the cell blank, a bar draws nothing.

     A line cannot do that and stay a line. A period that reported one category out of
     three has a single point, and a single point has nothing to join to - so the
     series rendered as an invisible dot and the chart looked as though that period had
     returned nothing at all, which is a worse misreading than the one this avoids.

     So on a line, absent counts as zero and the series stays continuous. The tooltip
     will say "Period 1: 0" for a category that period never reported; that is accepted
     here, deliberately, as the price of the line being drawn at all. Bar, donut and
     every file format keep the distinction. */
  const asContinuousLine = (values) =>
    (values || []).map((value) => (value === null || value === undefined ? 0 : value));

  const fetchChart = (chartType) => {
    /* Same options the editor preview draws with - see utils/chartOptions. The widget
       header already carries the name, so the chart draws no title of its own. */
    const isLine = String(chartType).toLowerCase() === 'line';
    const options = buildCartesianChartOptions({
      chartType,
      categories: formatDate(selectedValues?.map((value) => (value.name))),
      xTitle: charts?.groupBy?.fieldName,
      yTitle: charts?.fieldName?.fieldName,
    });

    const comparisonSeries = comparisonKeys.length ? seriesForPeriods() : null;

    const series =
      comparisonSeries && comparisonSeries.length
        ? comparisonSeries.map((one) => ({ ...one, data: isLine ? asContinuousLine(one.data) : one.data }))
        : [
            {
              name: charts?.fieldName?.fieldName || 'Value',
              /* the single-series path gets the same treatment - an ordinary report
                 with a gap in its data had the identical break in its line */
              data: isLine
                ? asContinuousLine(formatDate(selectedValues?.map((value) => value.value)))
                : formatDate(selectedValues?.map((value) => value.value)),
            },
          ];

    return (
      <ReactApexChart options={options} series={series} type={isLine ? 'area' : chartType.toLowerCase()}
        /* was a fixed 350 - the widget's card decides the height now, same as the editor
           preview, so a chart fills the space it was laid out with */
        height="100%" />
    )
  }

  /* ── DOWNLOAD ──
     A chart exports the series it plots — the group-by field and its summed value —
     rather than the rows underneath, because the series is what is on screen.

     CSV and XLS are written from that series. PDF and PNG photograph the chart instead:
     a shape cannot be spelled out in a table of numbers and still be the thing the reader
     was looking at. The photograph is of the chart BODY, not the whole widget, so the
     header bar and this very button stay out of the picture.

     ── WHICH ARRAY THE SERIES IS BUILT FROM ──
     `displayData`, the same one the chart draws from (see the series builders below) -
     NOT `reportData`, which is the untouched response.

     Those are two different arrays whenever a column data filter is set: the effect
     above writes the filtered rows into displayData and leaves reportData as it came
     back. Reading reportData here meant the picture formats and the data formats of one
     chart disagreed - PNG and PDF photograph the filtered chart, while CSV and XLS
     quietly wrote every row the API had returned. A reader filtering to one branch and
     downloading the sheet got the whole company back, with nothing to say so.

     No fallback to reportData when displayData is empty: a filter that excludes
     everything SHOULD export nothing, and the caller already reports that as "no data to
     download". Falling back would turn the strictest filter into no filter at all. */
  const chartExportContext = () => ({
    rows: displayData,
    groupByField: charts?.groupBy?.fieldName,
    valueField: charts?.fieldName?.fieldName,
    widgetName,
    boardName,
    // the global filters first - see the note on the table's exportContext
    filters: [...describeGlobalFilters(filterResource), ...describeDataFilters(dataFilters)],
    /* ── THE PERIODS, AS THE CHART DRAWS THEM ──
       comparisonKeys, NOT the raw periodKeys: a chart only draws a second series when a
       key column is set to match the rows on, and a file that broke the periods apart
       while the chart on screen showed one line would be a different report, not a copy
       of this one. Where it is empty the writers fall back to a single value column and
       nothing about an ordinary chart's file changes. */
    periodKeys: comparisonKeys,
    periodLabels,
  });

  const handleExport = (format) => {
    // an incomplete chart config has no series to write, which is a different problem
    // from an empty result and used to look identical
    if ((format === "csv" || format === "xlsx") && !(charts?.groupBy?.fieldName && charts?.fieldName?.fieldName)) {
      message.warning("This chart has no fields configured, so there is nothing to download");
      return "handled";
    }

    if (format === "csv") return exportChartToCsv(chartExportContext());
    if (format === "xlsx") return exportChartToXlsx(chartExportContext());
    // the picture carries the same heading as the other formats - see buildHeadingLines
    const naming = {
      widgetName,
      boardName,
      filters: [...describeGlobalFilters(filterResource), ...describeDataFilters(dataFilters)],
    };
    if (format === "pdf") return exportNodeToPdf({ node: chartBodyRef.current, ...naming });
    if (format === "png") return exportNodeToPng({ node: chartBodyRef.current, ...naming });
    return false;
  };

  /* ── displayData IS A DEPENDENCY, NOT AN OPTIONAL EXTRA ──
     The page header keeps whatever function this hands it until the effect runs again,
     so every value handleExport closes over has to be listed or the header holds a
     stale one.

     displayData is set by a LATER effect than the one that receives reportData, so on
     first paint it is still the initial []. Omitted from this list, the header kept the
     closure built from that empty array for the life of the widget: CSV and Excel
     reported "no data to download" off a chart that was visibly full. PDF and PNG were
     unaffected only because they read chartBodyRef.current, a ref resolved at click
     time rather than captured here. */
  useEffect(() => {
    if (typeof onRegisterExport === "function") onRegisterExport(handleExport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData, displayData, charts, widgetName, boardName, dataFilters, filterResource]);

  /* ── THE EMAIL ATTACHMENT ──
     A chart's PDF is a photograph of the rendered node, not a drawn table, so this goes
     through buildNodePdfAttachment rather than the table's buildRowsPdfAttachment. Same
     registration either way, which is what lets a board holding a table AND a chart send
     one email carrying both PDFs - see ReportWidjets.buildEmailAttachments.

     chartBodyRef is read at call time, not now: the dialog builds the attachment when
     Send is pressed, by which point the chart has certainly rendered. */
  const buildEmailAttachment = () =>
    buildNodePdfAttachment({
      node: chartBodyRef.current,
      widgetName,
      boardName,
      filters: [...describeGlobalFilters(filterResource), ...describeDataFilters(dataFilters)],
    });

  useEffect(() => {
    if (typeof onRegisterEmailAttachment === "function") {
      onRegisterEmailAttachment(buildEmailAttachment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData, charts, widgetName, boardName, dataFilters, filterResource]);

  const handleDownloadCsv = () => {
    const written = exportChartToCsv(chartExportContext());
    if (!written) {
      message.warning(
        charts?.groupBy?.fieldName && charts?.fieldName?.fieldName
          ? "There is no data to download for this widget"
          : "This chart has no fields configured, so there is nothing to download",
      );
    }
  };

  return (
    <div ref={widgetRef} className="board-chart-container">
      {/* A chart always carries its header. Unlike a table, whose columns say what it
          is, a chart is a shape - without the name above it there is nothing on screen
          identifying which resource it came from. widgetName was already passed in and
          simply not used, so the bar disappeared whenever the widget had no download or
          full-screen action. */}
      {/* ...unless the board is a report: it holds this one widget and the page header
          above already carries its name, so the bar only repeated it. */}
      {hideHeader ? null : (
        <WidgetHeader
          title={widgetName ?? ""}
          alwaysShow
          properties={widgetProperties}
          onDownload={handleDownloadCsv}
          onExport={handleExport}
          fullScreenTargetRef={widgetRef}
          /* This widget's own global filters, drawn as a second row INSIDE the header
             so the controls sit under the name of the chart they drive. Supplied only
             on a dashboard; a report's band is in the page toolbar. */
          filtersNode={filtersNode}
        />
      )}
      {/* A pie is drawn as the same donut the editor previews - see utils/chartOptions.

          Both chart types share one body element so the chart fills the card the layout
          gave it, exactly as .widget-content--chart does on the canvas. It used to be a
          bare <div id="chart"> for pies and nothing at all for the rest, which put a
          duplicate id on every pie widget and left the two types sized differently. */}
      <div className="board-chart-body" ref={chartBodyRef}>
        {/* ── NOTHING TO PLOT ───────────────────────────────────────────────────
            A chart handed no rows draws an empty canvas - a white rectangle the
            size of the widget, with no axes, no message and nothing to say whether
            the report is broken, still loading, or simply had no sales in the
            window asked for. That is indistinguishable from a bug, and was being
            reported as one.

            The commonest cause is a resource whose API returns nothing until its
            date parameters are filled in, so the reader lands on an empty board and
            has to be told to use the filter band above. */}
        {!hasRowsToPlot ? (
          <div className="board-chart-empty">
            <span className="board-chart-empty-icon">📊</span>
            <p className="board-chart-empty-title">No data to display</p>
            <p className="board-chart-empty-desc">
              This report returned no rows. Try choosing a date range above and clicking Apply.
            </p>
          </div>
        ) : charts?.chartType === 'Pie' && comparisonKeys.length > 1 ? (
          /* ── ONE DONUT PER PERIOD ──────────────────────────────────────────────
             A donut shows parts of ONE whole, and two periods are two wholes - put
             both in a single ring and every percentage is of the two added together,
             which is not a figure anyone asked for. Nested rings are worse: the radii
             read as magnitude and they are not.

             So each period gets its own ring, sized the same and labelled, and the
             comparison is in how the slices divide rather than in their sizes. Line
             and bar got multi-series free from ApexCharts; this is the one type that
             needs its own layout. */
          <div className="chart-donut-row">
            {comparisonKeys.map((key, index) => {
              const values = getValueArray(
                getTotal(
                  groupBy(formatValues(displayData, charts?.groupBy?.fieldName), charts?.groupBy?.fieldName),
                  index === 0 ? charts?.fieldName?.fieldName : `${key}__${charts?.fieldName?.fieldName}`,
                ),
              );

              return (
                <div className="chart-donut-cell" key={`donut_${key}`}>
                  <span className="chart-donut-title">{(periodLabels || {})[key] || key}</span>
                  <div className="chart-donut-body">
                    <ReactApexChart
                      options={buildDonutChartOptions({
                        labels: formatDate(values?.map((value) => value.name)) || [],
                      })}
                      /* A ring cannot draw a gap, so a period with no figure for a
                         slice contributes 0 to ITS OWN whole - which is the honest
                         reading here: the slice is absent from this period. */
                      series={(values || []).map((value) => Number(value.value) || 0)}
                      type="donut"
                      height="100%"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : charts?.chartType === 'Pie' ?
          <ReactApexChart
            options={buildDonutChartOptions({
              labels: formatDate(selectedValues?.map((value) => (value.name))) || [],
            })}
            series={(selectedValues || []).map((value) => Number(value.value))}
            type="donut"
            /* was a fixed 350, which overflowed a short widget and left dead space in a
               tall one - the card sizes it now, same as the editor preview */
            height="100%"
          />
          : charts?.chartType && fetchChart(charts?.chartType)}
      </div>
    </div>
  )

}

export default BoardChartContainer